import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as Logo } from "../../../assets/icons/svg/Auth/Logo-with-text.svg";
import { API_URLS } from '../../api/urls';
import AuthUtils from '../../utils/auth.utils';

const TwoFactorAuth: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';
  const rememberDevice = location.state?.rememberDevice || false;
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Focus first input on mount
  useEffect(() => {
    // Redirect to login if no email is provided
    if (!email) {
      navigate('/login', { replace: true });
      return;
    }
    inputRefs.current[0]?.focus();
  }, [email, navigate]);
  
  // Mask email: show first 3 chars and domain
  const maskEmail = (email: string): string => {
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 3) {
      return `${localPart}*****@${domain}`;
    }
    return `${localPart.substring(0, 3)}*****@${domain}`;
  };
  
  const maskedEmail = maskEmail(email);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
    
    if (e.key === 'Enter') {
      const isComplete = otp.every((digit) => digit !== '');
      if (isComplete) {
        handleVerify();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i] || '';
      }
      
      setOtp(newOtp);
      
      const nextEmptyIndex = newOtp.findIndex(digit => digit === '');
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[5]?.focus();
      }
    }
  };

  const handleVerify = async () => {
    if (otp.every((digit) => digit !== '')) {
      const otpString = otp.join('');
      
      setIsVerifying(true);
      setError('');
      
      try {
        const response = await fetch(API_URLS.VERIFY_OTP, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            otp: otpString,
          }),
        });

        const data = await response.json();
        
        if (data.success === 1) {
          console.log('OTP Verification Success:', data);
          
          // Create session with 6-hour expiration and remember device setting
          AuthUtils.createSession(data.admin_id, email, rememberDevice);
          
          // Navigate to dashboard on success
          navigate('/dashboard', { replace: true });
        } else {
          setError(data.message || 'Invalid OTP. Please try again.');
          setIsVerifying(false);
        }
      } catch (error) {
        setError('Verification error. Please try again.');
        setIsVerifying(false);
        console.error('OTP Verification Error:', error);
      }
    }
  };

  const handleResend = () => {
    console.log('Resending code...');
    setOtp(['', '', '', '', '', '']);
    setError('');
    inputRefs.current[0]?.focus();
  };

  const isOtpComplete = otp.every((digit) => digit !== '');

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="absolute top-[47px] left-[66px]">
        <div className="flex items-center gap-2">
          <Logo className="h-8 w-auto" />
        </div>
      </div>

      <div className="w-full max-w-[500px] bg-white rounded-2xl border border-[#2F2E38] shadow-md p-6 sm:p-8">
        <h1 className="text-2xl font-semibold text-black mb-2">
          Two steps verification (2FA)
        </h1>
        
        <p className="text-sm text-gray-600 mb-6">
          We sent a verification code to email{' '}
          <span className="text-black font-medium">{maskedEmail}</span>
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-black mb-3">
              Type your 6 digits security code
            </label>
            <div className="flex gap-3 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={isVerifying}
                  className={`w-12 h-12 text-center text-lg font-medium bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent ${
                    error ? 'border-red-500' : 'border-[#CFCFCF]'
                  } ${isVerifying ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-500 mb-2">{error}</p>
            )}
          </div>

          <div>
            <span className="text-sm text-gray-600">Didn't get the code? </span>
            <button
              onClick={handleResend}
              disabled={isVerifying}
              className="text-sm text-[#3B82F6] hover:text-[#2563EB] font-normal disabled:opacity-50"
            >
              Resend
            </button>
          </div>

          <button
            onClick={handleVerify}
            disabled={!isOtpComplete || isVerifying}
            className={`w-full py-3.5 rounded-lg text-[15px] font-medium transition-all mt-6 ${
              isOtpComplete && !isVerifying
                ? 'bg-black text-white hover:bg-gray-800 cursor-pointer'
                : 'bg-[#E5E5E5] text-gray-400 cursor-not-allowed'
            }`}
          >
            {isVerifying ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;